import type { TFunction } from 'i18next';
import { ChartColumnIcon, TrendingUpIcon, TriangleAlertIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ROLE } from '../../../common/role';
import { usePermissionChecker } from '../../../hooks/use-permission';
import { AccessDeniedView } from '../AccessDeniedView';

export const DASHBOARD_ID = {
	SENSOR_STATISTICS_DASHBOARD: 'SENSOR_STATISTICS_DASHBOARD',
	TPS_SENSOR_COMPUTATIONS_DASHBOARD: 'TPS_SENSOR_COMPUTATIONS_DASHBOARD',
	TPS_SECTION_CHART_DASHBOARD: 'TPS_SECTION_CHART_DASHBOARD',
	ALERT_LEVELS_DASHBOARD: 'ALERT_LEVELS_DASHBOARD',
} as const;
export type DASHBOARD_ID = (typeof DASHBOARD_ID)[keyof typeof DASHBOARD_ID];

const buildDashboards = (t: TFunction) => [
	{
		id: DASHBOARD_ID.SENSOR_STATISTICS_DASHBOARD,
		title: t('health.dashboards.sensorStatisticsTitle'),
		description: t('health.dashboards.sensorStatisticsDescription'),
		icon: ChartColumnIcon,
		iconBg: 'bg-blue-100',
		iconColor: 'text-blue-600',
	},
	{
		id: DASHBOARD_ID.TPS_SENSOR_COMPUTATIONS_DASHBOARD,
		title: t('health.dashboards.tpsComputationsTitle'),
		description: t('health.dashboards.tpsComputationsDescription'),
		icon: TrendingUpIcon,
		iconBg: 'bg-green-100',
		iconColor: 'text-green-600',
		permission: ROLE.SENSORS_TPS_PERFSTATS_READ,
	},
	{
		id: DASHBOARD_ID.TPS_SECTION_CHART_DASHBOARD,
		title: t('health.dashboards.tpsSectionChartTitle'),
		description: t('health.dashboards.tpsSectionChartDescription'),
		icon: TrendingUpIcon,
		iconBg: 'bg-purple-100',
		iconColor: 'text-purple-600',
		permission: ROLE.SENSORS_TPS_SECTION_READ,
	},
	{
		id: DASHBOARD_ID.ALERT_LEVELS_DASHBOARD,
		title: t('health.dashboards.alarmLevelsTitle'),
		description: t('health.dashboards.alarmLevelsDescription'),
		icon: TriangleAlertIcon,
		iconBg: 'bg-orange-100',
		iconColor: 'text-orange-600',
	},
];

export type DashboardsProps = {
	onSelectDashboard: (dashboardId: DASHBOARD_ID) => void;
	selectedDashboardId: DASHBOARD_ID | null;
};
export function Dashboards({ onSelectDashboard, selectedDashboardId }: DashboardsProps) {
	const { t } = useTranslation();
	const { hasPermission } = usePermissionChecker();
	const dashboards = useMemo(() => buildDashboards(t), [t]);
	const availableDashboards = dashboards.filter(
		(dashboard) => !dashboard.permission || hasPermission(dashboard.permission),
	);

	if (availableDashboards.length === 0) {
		return (
			<AccessDeniedView title={t('health.empty.noDashboardsTitle')} subtitle={t('health.empty.noDashboardsSubtitle')} />
		);
	}
	return (
		<div className="space-y-3">
			{availableDashboards.map((dashboard) => {
				const Icon = dashboard.icon;
				return (
					<Card
						key={dashboard.id}
						className={cn(
							'cursor-pointer transition-all hover:shadow-md border py-0',
							selectedDashboardId === dashboard.id &&
								'border-blue-500 bg-blue-50 dark:bg-blue-950 dark:border-blue-800 shadow-md',
						)}
						onClick={() => onSelectDashboard(dashboard.id)}
					>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className={`flex h-10 w-10 items-center justify-center rounded-lg ${dashboard.iconBg}`}>
									<Icon className={`h-5 w-5 ${dashboard.iconColor}`} />
								</div>

								<div>
									<h3 className="font-semibold">{dashboard.title}</h3>
									<p className="text-xs text-muted-foreground">{dashboard.description}</p>
								</div>
							</div>
						</CardContent>
					</Card>
				);
			})}
		</div>
	);
}
